// Token persistence. localStorage so every tab shares one session (the
// refresh token rotates with reuse detection — two tabs holding different
// generations would log each other out). Every access is wrapped: storage
// can be unavailable (private mode, blocked site data) and the panel must
// still work for the lifetime of the tab.

export interface TokenPair {
  access_token: string;
  access_expires_at: string;
  refresh_token: string;
  refresh_expires_at: string;
}

const KEY = "omf.tokens";
let memory: TokenPair | null = null;
const listeners = new Set<() => void>();

function isTokenPair(v: unknown): v is TokenPair {
  if (!v || typeof v !== "object") return false;
  const t = v as Record<string, unknown>;
  return (
    typeof t.access_token === "string" &&
    typeof t.refresh_token === "string" &&
    typeof t.access_expires_at === "string" &&
    typeof t.refresh_expires_at === "string"
  );
}

export function readTokens(): TokenPair | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (isTokenPair(parsed)) {
        memory = parsed;
        return parsed;
      }
    } else {
      memory = null;
    }
  } catch {
    // fall through to the in-memory copy
  }
  return memory;
}

export function writeTokens(t: TokenPair | null): void {
  memory = t;
  try {
    if (t) localStorage.setItem(KEY, JSON.stringify(t));
    else localStorage.removeItem(KEY);
  } catch {
    // in-memory only
  }
  listeners.forEach((l) => l());
}

export function onTokensChange(fn: () => void): () => void {
  listeners.add(fn);
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY || e.key === null) fn();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(fn);
    window.removeEventListener("storage", onStorage);
  };
}

export function isParsedTokenPair(v: unknown): v is TokenPair {
  return isTokenPair(v);
}

/** Milliseconds until the access token expires (negative when expired). */
export function accessTtlMs(t: TokenPair, now = Date.now()): number {
  const exp = Date.parse(t.access_expires_at);
  return Number.isNaN(exp) ? -1 : exp - now;
}
